import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Database, Loader2, ChevronDown, ChevronRight, RefreshCw,
  BookOpen, Globe, School, Tag, Layers, ArrowRight, X,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function authHdr(token) {
  return { Authorization: `Bearer ${token}` };
}

// ── Grade sort helper ─────────────────────────────────────────────────────────
const gradeNum = (g) => {
  const m = String(g).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 999;
};

// ── Summary card ─────────────────────────────────────────────────────────────
function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 flex items-center gap-3 border border-slate-200 shadow-none">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-xs text-slate-500 font-medium leading-tight">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">{value ?? "—"}</p>
      </div>
    </Card>
  );
}

// ── Sub-topic pill row ────────────────────────────────────────────────────────
function SubTopicPills({ subTopics }) {
  return (
    <tr>
      <td colSpan={7} className="px-6 pb-3 pt-0">
        <div className="flex flex-wrap gap-1.5 pl-1">
          {subTopics.map((st, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200"
            >
              {st.sub_topic || "—"}
              <span className="font-semibold text-slate-500">{st.count}</span>
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FuelRegistry({ token }) {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading,      setLoading]      = useState(true);
  const [data,         setData]         = useState(null);   // { summary, rows }
  const [baseOptions,  setBaseOptions]  = useState(null);   // from unfiltered load
  const [expanded,     setExpanded]     = useState({});     // rowKey → bool

  // Filters — initialise from URL params if present
  const [filterBoard,   setFilterBoard]   = useState(searchParams.get("board")   || "");
  const [filterGrade,   setFilterGrade]   = useState(searchParams.get("grade")   || "");
  const [filterSubject, setFilterSubject] = useState(searchParams.get("subject") || "");
  const [filterSource,  setFilterSource]  = useState("");

  const filtersActive = !!(filterBoard || filterGrade || filterSubject || filterSource);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (board, grade, subject, source) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (board)   params.set("board",   board);
      if (grade)   params.set("grade",   grade);
      if (subject) params.set("subject", subject);
      if (source)  params.set("source_school", source);

      const res = await fetch(
        `${API}/api/admin/cr/fuel-registry${params.toString() ? "?" + params.toString() : ""}`,
        { headers: authHdr(token) },
      );
      if (!res.ok) { toast.error("Failed to load Fuel Registry"); return; }
      const json = await res.json();
      setData(json);
      setExpanded({});
    } catch { toast.error("Network error"); }
    finally  { setLoading(false); }
  }, [token]);

  // Initial load (unfiltered) — capture base options
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    (async () => {
      setLoading(true);
      try {
        const res  = await fetch(`${API}/api/admin/cr/fuel-registry`, { headers: authHdr(token) });
        const json = await res.json();

        // Derive dropdown options from unfiltered rows
        const grades   = [...new Set((json.rows || []).map(r => r.grade).filter(Boolean))]
          .sort((a, b) => gradeNum(a) - gradeNum(b))
          .filter(g => g !== "—");
        const subjects = [...new Set((json.rows || []).map(r => r.subject).filter(g => g && g !== "—"))].sort();
        const sources  = [...new Set(
          (json.rows || []).flatMap(r =>
            r.sub_topics.map(s => s.source_school).filter(Boolean)
          )
        )].sort();

        setBaseOptions({ grades, subjects, sources, boards: json.summary?.distinct_boards || [] });

        // If URL params pre-set filters, fetch filtered data; else use unfiltered
        const initBoard   = searchParams.get("board")   || "";
        const initGrade   = searchParams.get("grade")   || "";
        const initSubject = searchParams.get("subject") || "";
        if (initBoard || initGrade || initSubject) {
          const filtered = await fetch(
            `${API}/api/admin/cr/fuel-registry?${new URLSearchParams({
              ...(initBoard   && { board:   initBoard }),
              ...(initGrade   && { grade:   initGrade }),
              ...(initSubject && { subject: initSubject }),
            })}`,
            { headers: authHdr(token) },
          );
          setData(await filtered.json());
        } else {
          setData(json);
        }
      } catch { toast.error("Failed to load Fuel Registry"); }
      finally  { setLoading(false); }
    })();
  }, [token, searchParams]);

  // Re-fetch when filters change (skip the very first render which is handled above)
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    fetchData(filterBoard, filterGrade, filterSubject, filterSource);
  }, [filterBoard, filterGrade, filterSubject, filterSource, fetchData]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const clearFilters = () => {
    setFilterBoard("");
    setFilterGrade("");
    setFilterSubject("");
    setFilterSource("");
  };

  const toggleRow = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const navParams = () => {
    const p = new URLSearchParams();
    if (filterBoard)   p.set("board",   filterBoard);
    if (filterGrade)   p.set("grade",   filterGrade);
    if (filterSubject) p.set("subject", filterSubject);
    return p.toString() ? "?" + p.toString() : "";
  };

  const summary = data?.summary || {};
  const rows    = data?.rows    || [];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 max-w-7xl mx-auto">

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Database className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Fuel Registry</h1>
            <p className="text-sm text-slate-500">Source-tagged question inventory across all boards</p>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => fetchData(filterBoard, filterGrade, filterSubject, filterSource)}
          disabled={loading}
          data-testid="fuel-registry-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <SummaryCard
          icon={BookOpen} label="Total Records"
          value={summary.total?.toLocaleString()}
          color="bg-indigo-500"
        />
        <SummaryCard
          icon={Globe} label="Boards"
          value={summary.distinct_boards?.length ?? "—"}
          color="bg-sky-500"
        />
        <SummaryCard
          icon={Tag} label="Topics"
          value={summary.distinct_topic_count?.toLocaleString()}
          color="bg-violet-500"
        />
        <SummaryCard
          icon={Layers} label="Shared Pool"
          value={summary.shared?.toLocaleString()}
          color="bg-emerald-500"
        />
        <SummaryCard
          icon={School} label="School-specific"
          value={summary.school_specific?.toLocaleString()}
          color="bg-amber-500"
        />
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 space-y-3">
        {/* Board pills */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Board</span>
          <button
            onClick={() => setFilterBoard("")}
            data-testid="board-pill-all"
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
              !filterBoard
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
            }`}
          >All</button>
          {(baseOptions?.boards || []).map(b => (
            <button
              key={b}
              onClick={() => setFilterBoard(b === filterBoard ? "" : b)}
              data-testid={`board-pill-${b}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                filterBoard === b
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
              }`}
            >{b}</button>
          ))}
        </div>

        {/* Grade + Subject + Source dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Grade</span>
            <select
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={filterGrade}
              onChange={e => setFilterGrade(e.target.value)}
              data-testid="grade-filter-select"
            >
              <option value="">All Grades</option>
              {(baseOptions?.grades || []).map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</span>
            <select
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={filterSubject}
              onChange={e => setFilterSubject(e.target.value)}
              data-testid="subject-filter-select"
            >
              <option value="">All Subjects</option>
              {(baseOptions?.subjects || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Source</span>
            <select
              className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
              data-testid="source-filter-select"
            >
              <option value="">All Sources</option>
              <option value="__shared__">Shared Pool</option>
              {(baseOptions?.sources || []).map(sc => (
                <option key={sc} value={sc}>{sc}</option>
              ))}
            </select>
          </div>

          {filtersActive && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors ml-1"
              data-testid="clear-filters-btn"
            >
              <X className="w-3.5 h-3.5" /> Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Scoped navigation buttons — visible only when filters are active */}
      {filtersActive && (
        <div className="flex items-center gap-2 mb-4" data-testid="scoped-nav-buttons">
          <span className="text-xs text-slate-500 font-medium">Jump to →</span>
          <Button
            size="sm" variant="outline"
            className="h-7 text-xs gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50"
            onClick={() => navigate(`/admin/dataset-health${navParams()}`)}
            data-testid="nav-dataset-health-scoped"
          >
            <Database className="w-3.5 h-3.5" /> Dataset Health <ArrowRight className="w-3 h-3" />
          </Button>
          <Button
            size="sm" variant="outline"
            className="h-7 text-xs gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50"
            onClick={() => navigate(`/admin/pool-readiness${navParams()}`)}
            data-testid="nav-pool-readiness-scoped"
          >
            <Layers className="w-3.5 h-3.5" /> Pool Readiness <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Result count */}
      {!loading && (
        <p className="text-xs text-slate-400 mb-2 font-medium">
          {rows.length} topic{rows.length !== 1 ? "s" : ""}
          {filtersActive ? " (filtered)" : " total"}
          {" · "}{summary.total?.toLocaleString()} records
        </p>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-2 border-slate-200">
          <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No records match the current filters</p>
        </Card>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white shadow-sm" data-testid="fuel-registry-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-8 px-3 py-3" />
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Board</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Grade</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Subject</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Topic</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600">Sub-topics</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-600">Records</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const key = `${r.board}|${r.grade}|${r.subject}|${r.topic}`;
                const open = !!expanded[key];
                return [
                  <tr
                    key={key}
                    className={`border-b border-slate-100 hover:bg-indigo-50/30 transition-colors cursor-pointer ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}
                    onClick={() => toggleRow(key)}
                    data-testid={`topic-row-${i}`}
                  >
                    <td className="px-3 py-3 text-slate-400">
                      {open
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className="text-xs px-2 py-0 bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                        {r.board}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-medium text-xs">{r.grade}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                        {r.subject}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-800 font-medium">{r.topic}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {r.sub_topic_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-700">{r.record_count}</td>
                    <td className="px-4 py-3">
                      {r.source === "Shared Pool"
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
                            <Globe className="w-3.5 h-3.5" /> Shared Pool
                          </span>
                        : <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium">
                            <School className="w-3.5 h-3.5" /> School-specific
                          </span>
                      }
                    </td>
                  </tr>,
                  open && (
                    <SubTopicPills key={`${key}-expand`} subTopics={r.sub_topics} />
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
